import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { CATEGORIES, catL10n, catPathFor } from "../lib/categories";
import { BRANDS, BRAND_SLUGS } from "../lib/brands";
import {
  brandPath,
  carDetailPath,
  canonicalBrandKey,
  lp,
  modelPath,
  modelSlug,
} from "../lib/seo";
import { whatsappLink } from "../lib/contact";
import { ShowroomSearch } from "./ShowroomSearch";

const SHOWCASE_KEYS = [
  "exotic",
  "sports",
  "luxury",
  "suv",
  "sports-suv",
  "convertible",
  "monthly",
];
const SHOWROOM_SLIDE_DURATION = 5000;
const SHOWROOM_TRANSITION_LEAD = 200;
const SHOWROOM_SLIDE_INTERVAL = SHOWROOM_SLIDE_DURATION - SHOWROOM_TRANSITION_LEAD;
const INITIAL_SHOWROOM_IMAGE = (
  `${process.env.PUBLIC_URL}/static/img/showroom-exotic-lcp-v1.webp`
);
const ALL_MODELS_QUERY_SUFFIXES = [
  "all-models",
  "tum-modelleri",
];

const SHOWROOM_IMAGE_OVERRIDES = {
  exotic: INITIAL_SHOWROOM_IMAGE,
  "sports-suv": (
    "https://customer-assets-cm19k8pv.emergentagent.net/" +
    "job_silver-drive-rentals/artifacts/xip5qkw5_sports%20suv.webp"
  ),
};

const SHOWROOM_NAV_LABELS = {
  "sports-suv": {
    EN: "Sport SUVs",
    TR: "Spor SUV'lar",
    RU: "Спорт SUV",
  },
  convertible: {
    EN: "Convertible Car Rental",
    TR: "Cabrio Araç Kiralama",
    RU: "Аренда кабриолета",
  },
};

const SHOWROOM_SLIDE_COPY = {
  EN: {
    exotic: {
      headline: "DOORS UP\nHEADS TURN",
      description: "Supercars that turn every arrival into an occasion, delivered across Dubai.",
    },
    sports: {
      headline: "BUILT FOR\nTHE THRILL",
      description: "Performance built for open roads, coastal drives and unforgettable weekends.",
    },
    luxury: {
      headline: "ARRIVE WITH\nINTENTION",
      description: "Exceptional comfort, effortless prestige and a first-class arrival.",
    },
    suv: {
      headline: "PRESENCE ON\nEVERY STREET",
      description: "Commanding luxury for family escapes, city streets and every destination.",
    },
    "sports-suv": {
      headline: "POWER WITH\nPURPOSE",
      description: "Supercar energy with everyday space, confidence and presence.",
    },
    convertible: {
      headline: "DUBAI\nROOF DOWN",
      description: "Grand touring made for winter sun, coast roads and long dinners out.",
    },
    monthly: {
      headline: "ONE MONTH\nONE KEY",
      description: "Flexible monthly plans on executive cars for residents and long stays.",
    },
  },
  TR: {
    exotic: {
      headline: "Kapılar yukarıda\nbakışlar üzerinizde",
      description: "Dubai genelinde teslim edilen, her anınızı heyecana dönüştüren süper otomobiller",
    },
    sports: {
      headline: "HEYECAN İÇİN\nTASARLANDI",
      description: "Açık yollar, sahil sürüşleri ve unutulmaz hafta sonları için tasarlanmış performans.",
    },
    luxury: {
      headline: "Tarzınızla\nfark yaratın",
      description: "Üstün konfor, zahmetsiz prestij ve birinci sınıf bir sürüş deneyimi",
    },
    suv: {
      headline: "HER SOKAKTA\nGÜÇLÜ DURUŞ",
      description: "Aile yolculukları, şehir sokakları ve her rota için iddialı lüks SUV'ler.",
    },
    "sports-suv": {
      headline: "KARAKTERLİ\nGÜÇ",
      description: "Süper otomobil enerjisi, günlük kullanım alanı ve her sürüşte güven.",
    },
    convertible: {
      headline: "AÇIK HAVADA\nDUBAİ KEYFİ",
      description: "Kış güneşi, sahil yolları ve uzun akşamlar için grand touring deneyimi.",
    },
    monthly: {
      headline: "Bir ay\ntek anahtar",
      description: "Dubai'de yaşayanlar ve uzun süreli konaklamalar için esnek aylık planlar.",
    },
  },
  RU: {
    exotic: {
      headline: "ДВЕРИ ВВЕРХ\nВЗГЛЯДЫ ВСЛЕД",
      description: "Суперкары, превращающие каждое появление в событие, с доставкой по Дубаю.",
    },
    sports: {
      headline: "СОЗДАН ДЛЯ\nОСТРЫХ ОЩУЩЕНИЙ",
      description: "Динамика для открытых дорог, прибрежных маршрутов и незабываемых выходных.",
    },
    luxury: {
      headline: "ПРИБЫВАЙТЕ\nСО СТИЛЕМ",
      description: "Исключительный комфорт, безупречный статус и первоклассное появление.",
    },
    suv: {
      headline: "СТАТУС НА\nКАЖДОЙ УЛИЦЕ",
      description: "Роскошные SUV для семейных поездок, городских улиц и любого маршрута.",
    },
    "sports-suv": {
      headline: "МОЩЬ С\nХАРАКТЕРОМ",
      description: "Энергия суперкара, ежедневный комфорт и уверенность в каждой поездке.",
    },
    convertible: {
      headline: "ДУБАЙ\nКРЫША ОТКРЫТА",
      description: "Гран-туринг для зимнего солнца, прибрежных дорог и долгих вечеров.",
    },
    monthly: {
      headline: "МЕСЯЦ\nОДИН КЛЮЧ",
      description: "Гибкие месячные планы для резидентов и длительного пребывания в Дубае.",
    },
  },
};

const SHOWROOM_COPY = {
  EN: {
    rent: {
      exotic: "Rent an Exotic Car",
      sports: "Rent a Sports Car",
      luxury: "Rent a Luxury Car",
      suv: "Rent a Luxury SUV",
      "sports-suv": "Rent a Sport SUV",
      convertible: "Rent a Convertible Car",
      monthly: "Rent a Car Monthly",
    },
    explore: {
      exotic: "Explore Exotic Cars",
      sports: "Explore Sports Cars",
      luxury: "Explore Luxury Cars",
      suv: "Explore Luxury SUVs",
      "sports-suv": "Explore Sport SUVs",
      convertible: "Explore Convertibles",
      monthly: "Explore Monthly Cars",
    },
    price: "From",
    perDay: "/ day",
    chat: "Chat on WhatsApp",
    search: "Search Ferrari, Urus, G63...",
    allBrandModels: "All {brand} models",
    previous: "Previous showroom category",
    next: "Next showroom category",
  },
  TR: {
    rent: {
      exotic: "Egzotik araç kirala",
      sports: "Spor araç kirala",
      luxury: "Lüks araç kirala",
      suv: "Lüks SUV kirala",
      "sports-suv": "Spor SUV kirala",
      convertible: "Cabrio araç kirala",
      monthly: "Aylık araç kirala",
    },
    explore: {
      exotic: "Egzotik araçları keşfet",
      sports: "Spor araçları keşfet",
      luxury: "Lüks araçları keşfet",
      suv: "Lüks SUV'ları keşfet",
      "sports-suv": "Spor SUV'ları keşfet",
      convertible: "Cabrio araçları keşfet",
      monthly: "Aylık kiralık araçları keşfet",
    },
    price: "Başlangıç fiyatı",
    perDay: "/ gün",
    chat: "WhatsApp'tan mesaj gönder",
    search: "Ferrari, Urus, G63 ara",
    allBrandModels: "Tüm {brand} modelleri",
    previous: "Önceki kategori",
    next: "Sonraki kategori",
  },
  RU: {
    rent: {
      exotic: "Аренда экзотического авто",
      sports: "Аренда спорткара",
      luxury: "Аренда люкс-авто",
      suv: "Аренда люкс-внедорожника",
      "sports-suv": "Аренда спортивного SUV",
      convertible: "Аренда кабриолета",
      monthly: "Аренда авто на месяц",
    },
    explore: {
      exotic: "Смотреть экзотические авто",
      sports: "Смотреть спорткары",
      luxury: "Смотреть люкс-авто",
      suv: "Смотреть люкс-внедорожники",
      "sports-suv": "Смотреть спорт-SUV",
      convertible: "Смотреть кабриолеты",
      monthly: "Смотреть авто на месяц",
    },
    price: "От",
    perDay: "/ день",
    chat: "Написать в WhatsApp",
    search: "Поиск Ferrari, Urus, G63...",
    allBrandModels: "Все модели {brand}",
    previous: "Предыдущая категория",
    next: "Следующая категория",
  },
};

export default function ShowroomHero({ cars, onWhatsAppVisibilityChange }) {
  const { lang, price } = useApp();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isProgressPaused, setIsProgressPaused] = useState(false);
  const [query, setQuery] = useState("");
  const whatsappButtonRef = useRef(null);
  const displayedSlideKeyRef = useRef(null);
  const remainingSlideTimeRef = useRef(SHOWROOM_SLIDE_INTERVAL);
  const slideTimerStartedAtRef = useRef(null);
  const copy = SHOWROOM_COPY[lang] || SHOWROOM_COPY.EN;
  const slideCopy = SHOWROOM_SLIDE_COPY[lang] || SHOWROOM_SLIDE_COPY.EN;
  const navLabelFor = (slide) => (
    SHOWROOM_NAV_LABELS[slide.category.key]?.[lang] || slide.label.name
  );
  const exploreLabelFor = (slide) => (
    copy.explore[slide.category.key] || copy.explore.luxury
  );
  const rentLabelFor = (slide) => (
    copy.rent[slide.category.key] || copy.rent.luxury
  );
  const slides = useMemo(() => SHOWCASE_KEYS.map((key) => {
    const category = CATEGORIES.find((item) => item.key === key);
    const matchingCars = cars.filter((car) => category.match(car));
    const availablePrices = matchingCars
      .map((car) => Number(car.daily_price_aed) || 0)
      .filter((dailyPrice) => dailyPrice > 0);
    const lowestPrice = availablePrices.length ? Math.min(...availablePrices) : null;

    return {
      category,
      label: catL10n(category, lang),
      headline: slideCopy[key].headline,
      description: slideCopy[key].description,
      image: SHOWROOM_IMAGE_OVERRIDES[key] || category.image,
      lowestPrice,
    };
  }), [cars, lang, slideCopy]);
  const activeSlide = slides[activeIndex];
  const activeSlideKey = activeSlide?.category?.key || null;
  const shouldAnimateSlide = Boolean(
    activeSlideKey &&
    displayedSlideKeyRef.current &&
    displayedSlideKeyRef.current !== activeSlideKey,
  );
  const searchMatches = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return { brandGroups: [], modelSuggestions: [] };
    }

    const matchingCars = cars.filter((car) => {
      const searchable = `${car.name} ${car.brand} ${car.model}`.toLowerCase();
      return searchable.includes(normalizedQuery);
    });
    const brandNames = [...new Set(
      cars
        .filter((car) => car.brand?.toLowerCase().includes(normalizedQuery))
        .map((car) => car.brand),
    )].sort();
    const brandGroups = brandNames.map((brand) => {
      const models = [...new Set(
        cars
          .filter((car) => car.brand === brand && car.model)
          .map((car) => car.model),
      )].sort();

      return { brand, models };
    });
    const seenModels = new Set();
    const modelSuggestions = matchingCars.reduce((items, car) => {
      if (!car.model) {
        return items;
      }

      const key = `${car.brand}-${car.model}`;
      if (!seenModels.has(key)) {
        seenModels.add(key);
        items.push({ brand: car.brand, model: car.model });
      }
      return items;
    }, []);

    return {
      brandGroups,
      modelSuggestions: brandGroups.length ? [] : modelSuggestions,
    };
  }, [cars, query]);

  useEffect(() => {
    if (!hasUserInteracted || !slides.length || isProgressPaused) {
      return undefined;
    }

    slideTimerStartedAtRef.current = window.performance.now();
    const timer = window.setTimeout(() => {
      remainingSlideTimeRef.current = SHOWROOM_SLIDE_INTERVAL;
      setActiveIndex((current) => (current + 1) % slides.length);
    }, remainingSlideTimeRef.current);

    return () => window.clearTimeout(timer);
  }, [activeIndex, hasUserInteracted, isProgressPaused, slides.length]);

  useEffect(() => {
    const beginCarousel = () => setHasUserInteracted(true);
    const interactionEvents = ["pointerdown", "keydown", "touchstart", "scroll"];

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, beginCarousel, {
        once: true,
        passive: true,
      });
    });

    return () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, beginCarousel);
      });
    };
  }, []);

  useEffect(() => {
    const button = whatsappButtonRef.current;

    if (!button || !onWhatsAppVisibilityChange) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      onWhatsAppVisibilityChange(entry.isIntersecting);
    }, { threshold: 0.15 });

    observer.observe(button);
    return () => observer.disconnect();
  }, [activeIndex, onWhatsAppVisibilityChange]);

  useEffect(() => {
    if (activeSlideKey) {
      displayedSlideKeyRef.current = activeSlideKey;
    }
  }, [activeSlideKey]);

  const changeSlide = (direction) => {
    setHasUserInteracted(true);
    remainingSlideTimeRef.current = SHOWROOM_SLIDE_INTERVAL;
    setIsProgressPaused(false);
    setActiveIndex((current) => (
      (current + direction + slides.length) % slides.length
    ));
  };

  const selectSlide = (index) => {
    setHasUserInteracted(true);
    remainingSlideTimeRef.current = SHOWROOM_SLIDE_INTERVAL;
    setIsProgressPaused(false);
    setActiveIndex(index);
  };

  const pauseProgress = (event) => {
    if (event.pointerType !== "mouse" || isProgressPaused) {
      return;
    }

    const elapsed = slideTimerStartedAtRef.current === null
      ? 0
      : window.performance.now() - slideTimerStartedAtRef.current;

    remainingSlideTimeRef.current = Math.max(
      0,
      remainingSlideTimeRef.current - elapsed,
    );
    setIsProgressPaused(true);
  };

  const resumeProgress = (event) => {
    if (event.pointerType === "mouse") {
      setIsProgressPaused(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const normalizedQuery = canonicalBrandKey(query);
    const matchedModel = cars.find((car) => (
      car.model && modelSlug(car.brand, car.model) === normalizedQuery
    ));

    if (matchedModel) {
      navigate(carDetailPath(matchedModel));
      return;
    }

    const knownBrandNames = BRAND_SLUGS.map((slug) => BRANDS[slug].name);
    const availableBrandNames = [...new Set(cars.map((car) => car.brand))];
    const matchedBrand = [...knownBrandNames, ...availableBrandNames].find((brand) => {
      const brandKey = canonicalBrandKey(brand);
      return (
        normalizedQuery === brandKey ||
        (
          normalizedQuery.startsWith(`${brandKey}-`) &&
          ALL_MODELS_QUERY_SUFFIXES.includes(normalizedQuery.slice(brandKey.length + 1))
        )
      );
    });

    if (matchedBrand) {
      navigate(brandPath(matchedBrand));
      return;
    }

    const search = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : "";
    navigate(`${lp("/rent-a-luxury-car-in-dubai")}${search}`);
  };

  const selectBrandModels = (brand) => {
    navigate(brandPath(brand));
  };

  const selectModel = (brand, model) => {
    const matchedCar = cars.find((car) => car.brand === brand && car.model === model);
    navigate(matchedCar ? carDetailPath(matchedCar) : modelPath(brand, model));
  };

  if (!activeSlide) {
    return null;
  }

  return (
    <section
      data-testid="showroom-hero"
      className="relative min-h-[760px] overflow-visible bg-[#090c0d] pt-[72px] text-white sm:min-h-[720px] lg:min-h-[820px]"
    >
      <div
        key={activeSlide.category.key}
        className={`showroom-slide absolute inset-0 ${
          shouldAnimateSlide ? "showroom-slide--enter" : ""
        }`}
      >
        <img
          src={activeSlide.image}
          alt={activeSlide.label.name}
          width="1536"
          height="1024"
          className={`showroom-image absolute bottom-0 right-0 top-0 h-full w-full object-cover object-center lg:w-[70%] ${
            activeSlide.category.key === "monthly" ? "showroom-image-monthly" : ""
          } ${shouldAnimateSlide ? "showroom-image--enter" : ""}`}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          sizes="100vw"
          elementtiming="showroom-hero-image"
        />
        <div className="showroom-overlay absolute inset-0" />
        <div className="showroom-ground-fade absolute bottom-0 right-0 h-[52%] w-full lg:w-[70%]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(5,8,9,0.92)_0%,rgba(5,8,9,0)_34%)]" />
      </div>

      <div className="relative mx-auto flex min-h-[688px] max-w-7xl flex-col px-4 pb-6 pt-12 sm:min-h-[648px] sm:px-8 sm:pt-16 lg:min-h-[748px] lg:pb-12 lg:pt-24">
        <div
          className={`showroom-copy max-w-[760px] ${
            shouldAnimateSlide ? "showroom-copy--enter" : ""
          }`}
        >
          <p
            data-testid="showroom-rent-label"
            className="flex items-center gap-3 font-mono-jb text-xs font-semibold uppercase tracking-[0.28em] text-[#99ddd4]"
          >
            <span className="h-px w-10 bg-[#89d3cb]" />
            {rentLabelFor(activeSlide)}
          </p>
          <h1 className="mt-6 max-w-xl whitespace-pre-line break-words font-cinzel text-4xl font-bold leading-[0.98] text-white sm:mt-8 sm:text-6xl lg:text-7xl">
            {navLabelFor(activeSlide)}
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-200 sm:mt-6 sm:text-lg">
            {activeSlide.description}
          </p>
          {activeSlide.lowestPrice && (
            <p
              data-testid="showroom-price"
              className="mt-7 font-mono-jb text-xs uppercase tracking-[0.2em] text-slate-300"
            >
              {copy.price} <span className="text-lg font-semibold text-white">{price(activeSlide.lowestPrice)}</span> {copy.perDay}
            </p>
          )}
          <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:flex-nowrap">
            <Link
              to={catPathFor(activeSlide.category)}
              data-testid="showroom-explore-category"
              className="inline-flex min-h-14 items-center justify-center gap-4 bg-[#86cec5] px-6 text-sm font-bold uppercase tracking-wide text-[#091112] transition-[background-color,transform] duration-200 hover:translate-x-1 hover:bg-white"
            >
              {exploreLabelFor(activeSlide)}
              <ArrowRight size={18} />
            </Link>
            <a
              ref={whatsappButtonRef}
              href={whatsappLink(`Hi Silver Drive, I'd like to rent a ${activeSlide.label.name} in Dubai.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="showroom-whatsapp-button"
              data-car-name={activeSlide.label.name}
              data-link-placement="showroom-whatsapp-button"
              className="inline-flex min-h-14 items-center justify-center gap-3 bg-[#25D366] px-5 text-sm font-semibold text-[#052e16] transition-[background-color] duration-200 hover:bg-[#1ebe5d]"
            >
              <MessageCircle size={18} />
              {copy.chat}
            </a>
          </div>
          <div
            data-testid="showroom-carousel-controls"
            className="mt-5 flex h-12 items-center gap-3 sm:gap-5"
          >
            <span data-testid="showroom-slide-counter" className="font-cinzel text-3xl text-white">
              {String(activeIndex + 1).padStart(2, "0")}
              <span className="ml-1 text-base text-slate-400">/ {String(slides.length).padStart(2, "0")}</span>
            </span>
            <div className="flex h-12 gap-2">
              <button
                type="button"
                onClick={() => changeSlide(-1)}
                data-testid="showroom-previous-button"
                aria-label={copy.previous}
                className="inline-flex h-12 w-12 items-center justify-center border border-white/40 text-white transition-colors hover:border-[#8bd6cd] hover:bg-white/10"
              >
                <ArrowLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => changeSlide(1)}
                data-testid="showroom-next-button"
                aria-label={copy.next}
                className="inline-flex h-12 w-12 items-center justify-center border border-white/40 text-white transition-colors hover:border-[#8bd6cd] hover:bg-white/10"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-6 pt-10 lg:absolute lg:inset-x-0 lg:bottom-12 lg:px-8 lg:pt-0">
          <ShowroomSearch
            copy={copy}
            query={query}
            searchMatches={searchMatches}
            setQuery={setQuery}
            onSubmit={submitSearch}
            onSelectBrand={selectBrandModels}
            onSelectModel={selectModel}
          />
          <div className="relative z-10 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-white/20 pt-5 sm:grid-cols-4 lg:grid-cols-7">
            {slides.map((slide, index) => (
              <button
                key={slide.category.key}
                type="button"
                onClick={() => selectSlide(index)}
                data-testid={`showroom-category-${slide.category.key}`}
                className="group text-left"
              >
                <span
                  data-testid={`showroom-progress-hit-area-${slide.category.key}`}
                  onPointerEnter={index === activeIndex ? pauseProgress : undefined}
                  onPointerLeave={index === activeIndex ? resumeProgress : undefined}
                  className="-my-1 block py-1"
                >
                  <span
                    className={`showroom-progress-track block h-0.5 overflow-hidden ${
                      index === activeIndex
                        ? "bg-white/25"
                        : "bg-white/25 group-hover:bg-white/60"
                    }`}
                  >
                    {index === activeIndex && hasUserInteracted && (
                      <span
                        key={activeIndex}
                        className={`showroom-progress-fill ${
                          isProgressPaused ? "showroom-progress-paused" : ""
                        }`}
                        style={{ animationDuration: `${SHOWROOM_SLIDE_INTERVAL}ms` }}
                      />
                    )}
                  </span>
                </span>
                <span className="mt-3 block font-mono-jb text-[10px] font-semibold tracking-[0.2em] text-[#a7e1db]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className={`mt-1 block break-words text-xs transition-colors duration-200 sm:text-sm ${
                  index === activeIndex ? "font-semibold text-white" : "text-slate-400 group-hover:text-white"
                }`}>
                  {navLabelFor(slide)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}